import { useState, useCallback, useEffect, useRef } from 'react';
import type { CrewMember } from '../types';
import { createCrewMember } from '../types';
import { useProfileContext } from './useProfileContext';
import { useRealtime } from './useRealtime';
import { apiGetCrewMeta, apiSaveCrew, ApiError } from '../api';
import { reportSaveError, reportSaveOk, isShowcaseMode } from '../lib/saveStatus';
import { pushConfigNotice, CONFIG_CONFLICT_MESSAGE } from '../lib/configGuard';

export function useCrew(bandId?: string) {
  const { activeProfileId } = useProfileContext();
  const { refreshToken } = useRealtime(bandId);
  const effectiveId = bandId || activeProfileId;
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipSave = useRef(true);
  // Anti-loop guard: snapshot of the last persisted/fetched state, so a
  // real-time refetch does not write the freshly-received data straight back.
  const lastSavedRef = useRef<string | null>(null);
  // Anti data-loss (v0.650): the `rider_crew.last_modified` this session loaded,
  // echoed on the PUT (`X-Rider-Version`) so the server can refuse a stale write
  // (409) instead of clobbering a fresher roster.
  const versionRef = useRef<number | null>(null);
  // Stable handle to `reload` (defined below) for the save error path (409).
  const reloadRef = useRef<() => Promise<void>>(async () => {});

  // --- Load crew from server when profile changes ---
  useEffect(() => {
    if (!effectiveId) {
      setCrew([]);
      setLoaded(true);
      skipSave.current = true;
      versionRef.current = null;
      return;
    }

    let cancelled = false;
    skipSave.current = true;
    setLoaded(false);

    (async () => {
      try {
        const { data, version } = await apiGetCrewMeta(effectiveId);
        if (!cancelled) {
          versionRef.current = version;
          lastSavedRef.current = JSON.stringify(data || []);
          setCrew(data || []);
          setLoaded(true);
          skipSave.current = false;
        }
      } catch (err) {
        console.error('[useCrew] Failed to load from API:', err);
        // SECURITY: on load failure, NEVER overwrite server data with [].
        if (!cancelled) {
          skipSave.current = true;
          reportSaveError('crew', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveId]);

  // --- Debounced save to server ---
  useEffect(() => {
    if (skipSave.current || !effectiveId || !loaded) return;
    // Showcase (demo) account: never autosave. (2026-09-15)
    if (isShowcaseMode()) return;

    // Anti-loop guard: skip when nothing changed since the last persist/fetch.
    if (JSON.stringify(crew) === lastSavedRef.current) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const sentVersion = versionRef.current;
      apiSaveCrew(effectiveId, crew, { version: sentVersion })
        .then((res) => {
          lastSavedRef.current = JSON.stringify(crew);
          if (typeof res.version === 'number') versionRef.current = res.version;
          reportSaveOk('crew');
        })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 409 && (err.body as { error?: string })?.error === 'stale') {
            pushConfigNotice('conflict', CONFIG_CONFLICT_MESSAGE);
            void reloadRef.current().catch(() => undefined);
            return;
          }
          console.error('[useCrew] Failed to save to API:', err);
          reportSaveError('crew', err);
        });
    }, 400);

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [crew, effectiveId, loaded]);

  const saveCrewToServer = useCallback((toSave: CrewMember[]) => {
    if (!effectiveId) return Promise.resolve();
    if (saveTimer.current) clearTimeout(saveTimer.current);
    return apiSaveCrew(effectiveId, toSave, { version: versionRef.current })
      .then((res) => {
        lastSavedRef.current = JSON.stringify(toSave);
        if (typeof res.version === 'number') versionRef.current = res.version;
        reportSaveOk('crew');
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 409 && (err.body as { error?: string })?.error === 'stale') {
          pushConfigNotice('conflict', CONFIG_CONFLICT_MESSAGE);
          void reloadRef.current().catch(() => undefined);
          return;
        }
        console.error('[useCrew] Failed to save to API:', err);
        reportSaveError('crew', err);
      });
  }, [effectiveId]);

  // Refetch from the server, keeping the SECURITY guard (never wipe on error).
  const reload = useCallback(async () => {
    if (!effectiveId) return;
    try {
      const { data, version } = await apiGetCrewMeta(effectiveId);
      versionRef.current = version;
      lastSavedRef.current = JSON.stringify(data || []);
      setCrew(data || []);
      setLoaded(true);
      skipSave.current = false;
      reportSaveOk('crew');
    } catch (err) {
      console.error('[useCrew] Failed to reload from API:', err);
      skipSave.current = true;
      reportSaveError('crew', err);
      throw err;
    }
  }, [effectiveId]);

  reloadRef.current = reload;

  // Real-time: refetch when another client updates this band (anti-loop guard
  // above + server-side self-broadcast suppression prevent a write-back loop).
  useEffect(() => {
    if (refreshToken > 0 && effectiveId) {
      void reload().catch(() => undefined);
    }
  }, [refreshToken, effectiveId, reload]);

  const addCrew = useCallback(
    (data?: Partial<CrewMember>) => {
      const m = createCrewMember(data);
      const updated = [...crew, m];
      setCrew(updated);
      return { crew: m, updated };
    },
    [crew],
  );

  const updateCrew = useCallback((id: string, data: Partial<CrewMember>) => {
    setCrew((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...data } : m)),
    );
  }, []);

  const deleteCrew = useCallback((id: string) => {
    setCrew((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const reorderCrew = useCallback((orderedIds: string[]) => {
    setCrew((prev) => {
      const map = new Map(prev.map((m) => [m.id, m]));
      return orderedIds
        .map((id, i): CrewMember | null => {
          const m = map.get(id);
          if (!m) return null;
          return { ...m, order: i };
        })
        .filter((m): m is CrewMember => m !== null);
    });
  }, []);

  const getCrew = useCallback(
    (id: string) => crew.find((m) => m.id === id) ?? null,
    [crew],
  );

  return {
    crew,
    addCrew,
    updateCrew,
    deleteCrew,
    reorderCrew,
    getCrew,
    saveCrewToServer,
    reload,
  };
}